import React from "react";
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  Chip,
  Link as MuiLink,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import MarketingSiteHeader from "./MarketingSiteHeader";
import MarketingSiteFooter from "./MarketingSiteFooter";

/**
 * Public careers page: job-board layout with no open roles (not hiring message).
 *
 * @returns {React.ReactElement}
 */
const CareersPage = () => {
  return (
    <Box sx={{ bgcolor: "background.default", color: "text.primary", minHeight: "100vh" }}>
      <MarketingSiteHeader />

      <Box component="main">
        <Box
          sx={{
            py: { xs: 6, md: 10 },
            background: "linear-gradient(135deg, rgba(74,0,224,0.06), rgba(249,72,74,0.06))",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Container maxWidth="md">
            <Typography variant="overline" color="secondary" fontWeight={700}>
              Careers
            </Typography>
            <Typography variant="h3" component="h1" fontWeight={800} sx={{ mt: 1, mb: 2 }}>
              Join the team building Quill AI
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75, maxWidth: 720 }}>
              We are a Toronto-based team focused on a best-in-class AI workspace for serious documents. When we post
              roles, they will appear here.
            </Typography>
          </Container>
        </Box>

        <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }} flexWrap="wrap" gap={2}>
            <Typography variant="h5" component="h2" fontWeight={800}>
              Open positions
            </Typography>
            <Chip icon={<WorkOutlineIcon fontSize="small" />} label="0 roles" size="small" variant="outlined" />
          </Stack>

          <Paper
            variant="outlined"
            sx={{
              p: { xs: 4, md: 6 },
              borderRadius: 3,
              borderStyle: "dashed",
              textAlign: "center",
              backgroundColor: "action.hover",
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
              We are not hiring right now
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: 520, mx: "auto", lineHeight: 1.7 }}>
              There are no open roles at Quill AI at the moment. This page will list job postings when we do—check back
              later if you would like to work with us.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              General inquiries:{" "}
              <MuiLink href="mailto:info@quillai.app" underline="hover" color="secondary">
                info@quillai.app
              </MuiLink>
            </Typography>
          </Paper>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 4, textAlign: "center" }}>
            We do not accept unsolicited agency resumes.
          </Typography>

          <Paper
            elevation={0}
            sx={{
              mt: 6,
              p: 4,
              borderRadius: 3,
              textAlign: "center",
              background: "linear-gradient(135deg, rgba(74,0,224,0.95), rgba(249,72,74,0.88))",
              color: "white",
            }}
          >
            <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
              Try the product
            </Typography>
            <Button
              component={RouterLink}
              to="/login"
              size="large"
              variant="contained"
              color="inherit"
              endIcon={<ArrowForwardIcon />}
              sx={{ color: "primary.main", textTransform: "none", fontWeight: 700 }}
            >
              Get started
            </Button>
          </Paper>
        </Container>
      </Box>

      <MarketingSiteFooter />
    </Box>
  );
};

export default CareersPage;
